import { format } from 'date-fns/format'
import { fromUnixTime } from 'date-fns/fromUnixTime'
import { startOfMonth } from 'date-fns/startOfMonth'
import { subMonths } from 'date-fns/subMonths'

import type {
  Account,
  AccountGroup,
  FinancialAccount,
  HistoricalAccountBalance,
  Member,
  MonthlyAccountBalance,
  NetWorth,
} from '../types'
import { AccountTypeName, AccountIcon, AccountIconVariants } from '../types'
import { isAccountLiability } from '../utils/AccountUtils'

const MAX_NUMBER_MONTHS = 13

export const filterAccounts = (accounts: Account[], members: Member[]) => {
  return accounts.filter((account) => {
    const { is_closed, is_deleted, is_hidden, is_manual } = account

    if (is_closed || is_deleted || is_hidden) return false
    if (is_manual || !account.guid) return true

    return members.some((member) => {
      return accounts.some((account) => {
        return member.guid === account.member_guid
      })
    })
  })
}

export const flattenBalances = (
  allBalances: MonthlyAccountBalance[],
  currentBalances: MonthlyAccountBalance[],
) => {
  return [...allBalances, ...currentBalances]
}

export const getLatestNetWorthData = (chartData: NetWorth[]) => {
  if (chartData && chartData.length > 0) {
    return chartData[chartData.length - 1]
  } else return undefined
}

export const sortByYearMonthAscending = (a: MonthlyAccountBalance, b: MonthlyAccountBalance) =>
  a.year_month - b.year_month

const buildDateTicks = () => {
  return Array.from({ length: MAX_NUMBER_MONTHS }, (_, count) => {
    return Math.floor(startOfMonth(subMonths(new Date(), count)).getTime() / 1000)
  }).sort()
}

export const buildChartData = (
  accounts: Account[],
  monthlyAccountBalances: MonthlyAccountBalance[],
  numberOfMonths: number,
) => {
  if (!accounts.length || !monthlyAccountBalances.length) return []
  const dateTicks = buildDateTicks()
  return dateTicks
    .reduceRight((result: any, x) => {
      const balances = monthlyAccountBalances.filter(
        ({ year_month }) => year_month === parseInt(format(fromUnixTime(x), 'yyyyMM')),
      )
      const accountsWithBalances = accounts.map((account) => {
        const balance = balances.find((balance) => balance.account_guid === account.guid)

        const zeroBalance = {
          change: 0,
          guid: account.guid,
          name: account.name,
          type: account.account_type,
          balance: 0,
        }

        if (!balance && result.length === 0 && !account.balance) return zeroBalance

        // The servers return the monthly account balances in Utah time. Frontend starts all the date calculations
        // in UTC. We get into overlaps for a few hours depending on the timezone with no monthly account balances
        // for all accounts.
        if (!balance && result.length === 0) {
          return {
            ...zeroBalance,
            value: isAccountLiability(account) ? -(account.balance ?? 0) : (account.balance ?? 0),
          }
        }

        if (!balance) {
          const lastBalance = result[0].accounts.find((acc: Account) => acc.guid === account.guid)

          return lastBalance ? lastBalance : zeroBalance
        }

        return {
          change: balance.transaction_total,
          guid: account.guid,
          name: account.name,
          type: account.account_type,
          value: balance.balance,
        }
      })

      const { y, change } = accountsWithBalances.reduce(
        (acc, current) => {
          return {
            y: acc.y + (current.value || 0),
            change: acc.change + (current.change || 0),
          }
        },
        { y: 0, change: 0 },
      )

      return [
        {
          accounts: accountsWithBalances,
          x,
          y,
          change,
        },
        ...result,
      ]
    }, [])
    .slice(MAX_NUMBER_MONTHS - numberOfMonths)
}

export const buildGainsLosses = (selectedData?: NetWorth) => {
  const gains: { accountName: string; gain: number }[] = []
  const losses: { accountName: string; loss: number }[] = []
  let totalGains = 0
  let totalLosses = 0

  selectedData?.accounts.forEach((account) => {
    if (account.change > 0) {
      gains.push({ accountName: account.name, gain: account.change })
      totalGains += account.change
    }
    if (account.change < 0) {
      losses.push({ accountName: account.name, loss: account.change })
      totalLosses += account.change
    }
  })

  return { netWorth: selectedData, gains, losses, totalGains, totalLosses }
}

export const buildAssetsLiabilities = (accounts: Account[]) => {
  const assets: FinancialAccount[] = []
  const liabilities: FinancialAccount[] = []
  let totalAssets = 0
  let totalLiabilities = 0

  accounts.forEach((account) => {
    if (isAccountLiability(account)) {
      const balance = account.balance ?? 0
      liabilities.push({ accountName: account.name, accountType: account.account_type, balance })
      totalLiabilities += balance
    } else {
      const balance = account.balance ?? 0
      assets.push({ accountName: account.name, accountType: account.account_type, balance })
      totalAssets += balance
    }
  })

  return { assets, liabilities, totalAssets, totalLiabilities }
}
export const toMonthlyAccountBalance = (hab: HistoricalAccountBalance): MonthlyAccountBalance => ({
  account_guid: hab.account_guid,
  balance: hab.balance,
  credit_transaction_total: 0,
  debit_transaction_total: 0,
  id: hab.guid,
  month: hab.month,
  transaction_total: hab.transaction_total ?? 0,
  user_guid: hab.user_guid,
  year: hab.year,
  year_month: hab.year_month,
})

// groups accounts by account type
// returns array of account groups, each with an icon, name, and the grouped accounts
export const groupAccounts = (accounts: FinancialAccount[]): AccountGroup[] => {
  const groupedAccounts = accounts.reduce<{ [key: number]: FinancialAccount[] }>((acc, account) => {
    const accountType = account.accountType
    if (!acc[accountType]) {
      acc[accountType] = []
    }
    acc[accountType].push(account)
    return acc
  }, {})

  return Object.entries(groupedAccounts).map(([accountType, accounts]) => ({
    icon: AccountIconVariants[+accountType] || AccountIcon[+accountType],
    name: AccountTypeName[+accountType],
    accounts,
  }))
}
